import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkDetailPage from '../../../components/work/WorkDetailPage';
import WorkFilePanel from '../../../components/work/shared/WorkFilePanel';
import WorkCommentPanel from '../../../components/work/shared/WorkCommentPanel';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import {
  contractsApi,
  contractDiscussionsApi,
  contractFilesApi,
  contractRenewApi,
} from '../../../utils/workApi';
import {
  buildContractDiscussionCreatePayload,
  buildContractDiscussionUpdatePayload,
  buildContractRenewCreatePayload,
  buildContractRenewUpdatePayload,
} from '../../../utils/workPayloadBuilders';
import { getContractFromResponse, mapContractRenewToForm } from '../../../utils/workResponseUtils';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import {
  Button,
  Card,
  Descriptions,
  Tag,
  message,
  Tabs,
  Table,
  Modal,
  Form,
  Upload,
  Space,
  Popconfirm,
  Spin,
  Tooltip,
} from 'antd';
import { FileProtectOutlined, EditOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import PermissionGate from '../../../components/PermissionGate';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import {
  getContractSignatureImageUrl,
  resolveContractStatusLabel,
} from '../../../utils/contractSignatureUtils';
import { stripHtml } from '../../../utils/stripHtml';
import { toJalali } from '../../../utils/dateUtils';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const formatContractDate = (value) => {
  if (!value) return '—';
  return toJalali(value) || String(value).slice(0, 10);
};

const formatContractDateTime = (value) => {
  if (!value) return '—';
  const day = toJalali(value);
  if (!day) return String(value);
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const time = d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
      return `${day} ${time}`;
    }
  } catch {
    /* ignore */
  }
  return day;
};

const resolveSignerName = (contract) => {
  const by = contract?.sign_by ?? contract?.signBy ?? contract?.signed_by;
  if (by && typeof by === 'object') return by.name || by.email || '—';
  if (typeof by === 'string' && by.trim()) return by;
  if (typeof by === 'number') return `کاربر #${by}`;
  return '—';
};

const resolveRenewAddedBy = (row) => {
  const by = row?.renewed_by;
  if (by && typeof by === 'object') return by.name || by.email || '—';
  if (typeof by === 'string') return by;
  return '—';
};

const resolveRenewAddedAt = (row) =>
  row?.created_at || row?.added_at || row?.start_date || null;

const TabToolbar = ({ children }) => (
  <div className="flex justify-end mb-4" style={{ fontFamily: 'CustomFont' }}>
    {children}
  </div>
);

const ViewContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBack = () =>
    navigate(returnToRef.current || '/work/contracts', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const [contract, setContract] = useState(null);
  const [company, setCompany] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/contracts',
    listLabel: 'قراردادها',
    entityLabel:
      contract?.subject ||
      contract?.contract_number ||
      (id ? `قرارداد #${id}` : 'قرارداد'),
  });
  const [renewModal, setRenewModal] = useState(false);
  const [editRenewId, setEditRenewId] = useState(null);
  const [signing, setSigning] = useState(false);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewForm] = Form.useForm();

  const signatureImageUrl = getContractSignatureImageUrl(contract?.company_signature);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await contractsApi.getById(id);
        const c = getContractFromResponse(res);
        setContract(c);
        setCompany(res?.company || c?.company || null);
      } catch {
        message.error('خطا در دریافت قرارداد');
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    load();
  }, [load]);

  const signContract = async (uploadFile) => {
    const imageFile = uploadFile?.originFileObj ?? uploadFile;
    if (!imageFile) return false;
    setSigning(true);
    try {
      await contractsApi.sign(id, imageFile);
      message.success('امضا ثبت شد');
      await load({ silent: true });
    } catch {
      message.error('خطا در ثبت امضا');
    } finally {
      setSigning(false);
    }
    return false;
  };

  const submitRenew = async () => {
    let v;
    try {
      v = await renewForm.validateFields();
    } catch {
      return;
    }
    setRenewSubmitting(true);
    try {
      if (editRenewId) {
        await contractRenewApi.update(editRenewId, buildContractRenewUpdatePayload(v));
      } else {
        await contractRenewApi.create(buildContractRenewCreatePayload(v, id));
      }
      message.success('تمدید ذخیره شد');
      setRenewModal(false);
      await load({ silent: true });
    } catch {
      message.error('خطا در ذخیره تمدید');
    } finally {
      setRenewSubmitting(false);
    }
  };

  const submitDiscussion = async (text) => {
    await contractDiscussionsApi.create(buildContractDiscussionCreatePayload(text, id));
    await load({ silent: true });
  };

  const openCreateRenew = () => {
    setEditRenewId(null);
    renewForm.resetFields();
    setRenewLoading(false);
    setRenewModal(true);
  };

  const openEditRenew = async (row) => {
    setEditRenewId(row.id);
    renewForm.resetFields();
    setRenewModal(true);
    setRenewLoading(true);
    try {
      const res = await contractRenewApi.getEditData(row.id);
      renewForm.setFieldsValue(mapContractRenewToForm(res?.renew || row));
    } catch {
      message.error('خطا در بارگذاری تمدید');
      setRenewModal(false);
      setEditRenewId(null);
    } finally {
      setRenewLoading(false);
    }
  };

  const discussions = contract?.discussion || [];
  const files = contract?.files || [];
  const renewHistory = contract?.renew_history || [];
  const clientId = contract?.client_id || contract?.client?.id;
  const statusLabel = resolveContractStatusLabel(contract);
  const descriptionText = stripHtml(contract?.description || contract?.contract_detail) || '—';
  const signerName = resolveSignerName(contract);
  const signDateLabel = formatContractDateTime(contract?.sign_date);

  return (
    <WorkDetailPage
      title={contract?.subject || 'جزئیات قرارداد'}
      subtitle={contract?.contract_number ? `شماره: ${contract.contract_number}` : 'قرارداد'}
      icon={FileProtectOutlined}
      iconClassName="bg-amber-50 dark:bg-amber-900/20"
      iconColorClass="text-amber-600 dark:text-amber-400"
      backPath="/work/contracts"
      onBack={goBack}
      loading={loading}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
      headerExtra={
        <Space wrap style={{ fontFamily: 'CustomFont' }}>
          {!signatureImageUrl ? (
            <PermissionGate permission="edit_contract">
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={signContract}
                disabled={signing}
              >
                <Button icon={<UploadOutlined />} loading={signing} style={{ fontFamily: 'CustomFont' }}>
                  ثبت امضای شرکت
                </Button>
              </Upload>
            </PermissionGate>
          ) : null}
          <PermissionGate permission="edit_contract">
            <Button
              type="primary"
              icon={<EditOutlined />}
              className={PANEL_PRIMARY_BTN_CLASS}
              onClick={() =>
                navigateWithTrail(
                  `/work/contracts/${id}/edit`,
                  contract?.subject
                    ? `ویرایش ${contract.subject}`
                    : `ویرایش قرارداد #${id}`,
                  {
                    returnTo: returnToRef.current || '/work/contracts',
                    ...buildGoBackState(),
                  },
                )
              }
              style={{ fontFamily: 'CustomFont' }}
            >
              ویرایش
            </Button>
          </PermissionGate>
          <PermissionGate permission="delete_contract">
            <Popconfirm
              title={<span style={{ fontFamily: 'CustomFont' }}>حذف قرارداد؟</span>}
              onConfirm={async () => {
                await contractsApi.delete(id);
                message.success('حذف شد');
                navigate(returnToRef.current || '/work/contracts', { state: buildGoBackState() });
              }}
            >
              <Button danger icon={<DeleteOutlined />} style={{ fontFamily: 'CustomFont' }}>
                حذف
              </Button>
            </Popconfirm>
          </PermissionGate>
        </Space>
      }
    >
      <Spin spinning={refreshing}>
        <Card className="shadow-sm border-0 mb-4 bg-white dark:bg-gray-800">
          <Descriptions
            bordered
            column={{ xs: 1, sm: 2 }}
            size="small"
            labelStyle={{ fontFamily: 'CustomFont' }}
            contentStyle={{ fontFamily: 'CustomFont' }}
          >
            <Descriptions.Item label={label('مشتری')}>
              {clientId ? (
                <button
                  type="button"
                  className="text-indigo-600 font-medium bg-transparent border-0 p-0 cursor-pointer"
                  style={{ fontFamily: 'CustomFont' }}
                  onClick={() =>
                    navigateWithTrail(
                      `/clients/${clientId}`,
                      contract?.client?.name || 'مشتری',
                      { returnTo: `/work/contracts/${id}` },
                    )
                  }
                >
                  {contract?.client?.name || '—'}
                </button>
              ) : (
                <span style={{ fontFamily: 'CustomFont' }}>{contract?.client?.name || '—'}</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={label('نوع قرارداد')}>
              <span style={{ fontFamily: 'CustomFont' }}>{contract?.contract_type?.name || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label={label('مبلغ')}>
              <span style={{ fontFamily: 'CustomFont' }}>
                {formatWorkPriceDisplay(contract?.amount, {
                  suffix: contract?.currency?.currency_symbol,
                })}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={label('پروژه')}>
              <span style={{ fontFamily: 'CustomFont' }}>{contract?.project?.project_name || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label={label('شرکت')}>
              <span style={{ fontFamily: 'CustomFont' }}>{company?.company_name || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label={label('وضعیت')}>
              <Tag style={{ fontFamily: 'CustomFont' }}>{statusLabel}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={label('تاریخ شروع')}>
              <span style={{ fontFamily: 'CustomFont' }}>
                {formatContractDate(contract?.original_start_date || contract?.start_date)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={label('تاریخ پایان')}>
              <span style={{ fontFamily: 'CustomFont' }}>
                {formatContractDate(contract?.original_end_date || contract?.end_date)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={label('توضیحات')} span={2}>
              <span style={{ fontFamily: 'CustomFont' }} className="whitespace-pre-wrap">
                {descriptionText}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="shadow-sm border-0 bg-white dark:bg-gray-800">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'files',
                label: <span style={{ fontFamily: 'CustomFont' }}>فایل‌ها</span>,
                children: (
                  <WorkFilePanel
                    files={files}
                    onUpload={(fl) => contractFilesApi.upload(id, fl).then(() => load({ silent: true }))}
                    onDelete={(fid) => contractFilesApi.delete(fid).then(() => load({ silent: true }))}
                    getDownloadUrl={contractFilesApi.downloadUrl}
                  />
                ),
              },
              {
                key: 'discussion',
                label: <span style={{ fontFamily: 'CustomFont' }}>بحث</span>,
                children: (
                  <WorkCommentPanel
                    comments={discussions}
                    onCreate={submitDiscussion}
                    onUpdate={async (discId, text) => {
                      await contractDiscussionsApi.update(
                        discId,
                        buildContractDiscussionUpdatePayload(text),
                      );
                      await load({ silent: true });
                    }}
                    onDelete={async (discId) => {
                      await contractDiscussionsApi.delete(discId);
                      await load({ silent: true });
                    }}
                  />
                ),
              },
              {
                key: 'renew',
                label: <span style={{ fontFamily: 'CustomFont' }}>تمدید</span>,
                children: (
                  <>
                    <TabToolbar>
                      <Button
                        type="primary"
                        className={PANEL_PRIMARY_BTN_CLASS}
                        onClick={openCreateRenew}
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        تمدید جدید
                      </Button>
                    </TabToolbar>
                    <Table
                      rowKey="id"
                      dataSource={renewHistory}
                      pagination={false}
                      scroll={{ x: true }}
                      columns={[
                        {
                          title: label('شروع'),
                          dataIndex: 'start_date',
                          render: (v) => (
                            <span style={{ fontFamily: 'CustomFont' }}>{formatContractDate(v)}</span>
                          ),
                        },
                        {
                          title: label('پایان'),
                          dataIndex: 'end_date',
                          render: (v) => (
                            <span style={{ fontFamily: 'CustomFont' }}>{formatContractDate(v)}</span>
                          ),
                        },
                        {
                          title: label('مبلغ'),
                          dataIndex: 'amount',
                          render: (v) => (
                            <span style={{ fontFamily: 'CustomFont' }}>{formatWorkPriceDisplay(v)}</span>
                          ),
                        },
                        {
                          title: label('اضافه‌کننده'),
                          render: (_, row) => (
                            <span style={{ fontFamily: 'CustomFont' }}>{resolveRenewAddedBy(row)}</span>
                          ),
                        },
                        {
                          title: label('تاریخ'),
                          render: (_, row) => (
                            <span style={{ fontFamily: 'CustomFont' }}>
                              {formatContractDate(resolveRenewAddedAt(row))}
                            </span>
                          ),
                        },
                        workActionsColumn((_, row) => (
                          <Space>
                            <Tooltip title="ویرایش">
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => openEditRenew(row)}
                              />
                            </Tooltip>
                            <Popconfirm
                              title={<span style={{ fontFamily: 'CustomFont' }}>حذف تمدید؟</span>}
                              onConfirm={async () => {
                                await contractRenewApi.delete(row.id);
                                message.success('حذف شد');
                                await load({ silent: true });
                              }}
                              okText="حذف"
                              cancelText="لغو"
                              okButtonProps={{ danger: true }}
                            >
                              <Tooltip title="حذف">
                                <Button type="text" danger icon={<DeleteOutlined />} />
                              </Tooltip>
                            </Popconfirm>
                          </Space>
                        )),
                      ]}
                    />
                  </>
                ),
              },
            ]}
          />
        </Card>

        {signatureImageUrl ? (
          <Card className="shadow-sm border-0 mt-4 bg-white dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4" style={{ fontFamily: 'CustomFont' }}>
              <img
                src={signatureImageUrl}
                alt="امضای شرکت"
                className="max-h-24 max-w-[220px] object-contain rounded border border-gray-200 dark:border-gray-600 bg-white p-2"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="space-y-1">
                <div className="font-semibold text-gray-800 dark:text-gray-100">امضای شرکت</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  امضاکننده: {signerName}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  تاریخ امضا: {signDateLabel}
                </div>
              </div>
            </div>
          </Card>
        ) : null}
      </Spin>

      <Modal
        open={renewModal}
        title={<span style={{ fontFamily: 'CustomFont' }}>{editRenewId ? 'ویرایش تمدید' : 'تمدید قرارداد'}</span>}
        onCancel={() => !renewSubmitting && !renewLoading && setRenewModal(false)}
        onOk={submitRenew}
        confirmLoading={renewSubmitting}
        okButtonProps={{
          className: PANEL_PRIMARY_BTN_CLASS,
          loading: renewSubmitting,
          disabled: renewLoading,
        }}
        cancelButtonProps={{ style: { fontFamily: 'CustomFont' }, disabled: renewSubmitting || renewLoading }}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnClose
      >
        <Spin spinning={renewLoading} tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری...</span>}>
          <Form form={renewForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="start_date" label={label('شروع')} rules={[{ required: true }]}>
              <PersianDatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="end_date" label={label('پایان')}>
              <PersianDatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="amount" label={label('مبلغ')} rules={[{ required: true }]}>
              <WorkPriceInput />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </WorkDetailPage>
  );
};

export default ViewContract;
